import { api } from "./api";
import { isNativeApp } from "./native";
import type { PushSubscriptionInput } from "@shared/types";

// Native app: reminders are LOCAL notifications (scheduled on-device, no
// server, works offline). Web/PWA keeps Web Push + the reminders cron worker.
// One interface for both — the settings UI doesn't care which path runs.

// Scheduling moved to lib/reminderScheduler.ts, which plans every kind through
// the suppression engine. 1001 belonged to the weigh-in-only scheduler that
// preceded it; a device upgrading from an older build still has that one
// pending, and left alone it would fire beside its replacement.
const LEGACY_REMINDER_ID = 1001;

async function nativeNotifications() {
  // NEVER return the plugin proxy from an async function: promise resolution
  // probes the value's `.then`, Capacitor's proxy turns that probe into a
  // native "then()" call that doesn't exist, and the await deadlocks forever
  // (this froze onboarding's notification ask on device). The module
  // namespace is a plain object — safe to await; destructure at call sites.
  return await import("@capacitor/local-notifications");
}

export async function cancelLegacyReminder(): Promise<void> {
  if (!isNativeApp()) return;
  const { LocalNotifications: ln } = await nativeNotifications();
  await ln.cancel({ notifications: [{ id: LEGACY_REMINDER_ID }] }).catch(() => { /* none */ });
}

export function pushSupported(): boolean {
  if (isNativeApp()) return true; // local notifications
  return typeof navigator !== "undefined"
    && "serviceWorker" in navigator
    && typeof window !== "undefined"
    && "PushManager" in window
    && "Notification" in window;
}

export function isIos(): boolean {
  return typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** iOS allows Web Push only for installed (Home-Screen / standalone) PWAs. */
export function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mm = window.matchMedia?.("(display-mode: standalone)").matches ?? false;
  const iosStandalone = (navigator as unknown as { standalone?: boolean }).standalone === true;
  return mm || iosStandalone;
}

function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out as Uint8Array<ArrayBuffer>;
}

/** Request permission, subscribe, and register the subscription server-side.
 *  Throws "permission-denied" or "no-vapid-key" on failure. */
export async function enablePush(): Promise<void> {
  if (isNativeApp()) {
    const { LocalNotifications: ln } = await nativeNotifications();
    const { display } = await ln.requestPermissions();
    if (display !== "granted") throw new Error("permission-denied");
    return;
  }
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("permission-denied");
  const reg = await navigator.serviceWorker.ready;
  const { key } = await api.pushKey();
  if (!key) throw new Error("no-vapid-key");
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  const j = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
  const input: PushSubscriptionInput = { endpoint: j.endpoint, keys: { p256dh: j.keys.p256dh, auth: j.keys.auth } };
  await api.pushSubscribe(input);
}

export async function disablePush(): Promise<void> {
  if (isNativeApp()) {
    // Cancelling the schedule is reminderScheduler's job; only the leftover
    // from the old build is this module's to clear.
    await cancelLegacyReminder();
    return;
  }
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => { /* best-effort */ });
    await sub.unsubscribe().catch(() => { /* best-effort */ });
  }
}

export function browserTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
}
